import { Service } from '@genial/services';
import { MetaResponse } from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { selectorFamily } from 'recoil';

import { blankMetaResponse } from '../../../utils/blankMetaResponse';
import { clubsLoadedState } from '../atom';
import { screenNameCustomerApplication } from '../constants';
import { execFilter } from '../external/filters';
import {
  ReturnTypeOf_ObterClube,
  ReturnTypeOf_ObterDatasClubes,
  ReturnTypeOf_ProdutosObterProdutosDisponiveis,
} from '../external/types';
import { normalizeClubs, normalizeClubsDate, normalizeClubsDetails } from '../normalizer/clubs';
import {
  ClubsDetailsEntityNormalized,
  DynamicAssetsQueryParams,
  ClubsEntityNormalized,
  DynamicCustomFiltersProps,
  DynamicAssetsQueryCustomerParams,
  ClubsDateEntityNormalized,
} from '../types';

export const clubsQueryUpdated = selectorFamily<
  { data: ClubsEntityNormalized[]; meta: MetaResponse },
  DynamicAssetsQueryParams
>({
  key: 'CustomerApplication/clubsQueryUpdated',
  get: ({ accountId, filtersToApply, searchTerm }) => {
    return async ({ get }) => {
      const loadedClubs = get(clubsLoadedState);

      let data: ClubsEntityNormalized[] = [];

      if (!loadedClubs.data.length || searchTerm) {
        const apiData =
          await Service.External.Extranet.Produtos.V1.produtosObterProdutosDisponiveis({
            filtro: {
              idConta: accountId,
              nomeProduto: searchTerm,
              tipoProduto: 7,
            },
            screenName: screenNameCustomerApplication,
          });

        data = normalizeClubs(apiData as ReturnTypeOf_ProdutosObterProdutosDisponiveis);
      } else {
        data = loadedClubs.data;
      }

      if (filtersToApply) {
        data = execFilter(data, filtersToApply);
      }

      return { data, meta: blankMetaResponse() };
    };
  },
});

export const clubsCustomFiltersQuery = selectorFamily<
  DynamicCustomFiltersProps,
  DynamicAssetsQueryCustomerParams
>({
  key: 'CustomerApplication/clubsCustomFiltersQuery',
  get: () => {
    return async ({ get }) => {
      const loadedClubs = get(clubsLoadedState);

      if (!loadedClubs.data.length) {
        return { filters: [] };
      }

      const offerTypeOptions = loadedClubs.data.reduce((acc, { fundId, managerNameNormalized }) => {
        const existingOption = acc.find((option) => option.value === fundId.toString());
        if (!existingOption) {
          acc.push({ label: managerNameNormalized, value: managerNameNormalized });
        }
        return acc;
      }, [] as { label: string; value: string }[]);

      return {
        filters: [{ fieldName: 'fundId', label: 'Gestor', options: offerTypeOptions }],
      };
    };
  },
});

export const clubsDetailsQuery = selectorFamily<
  ClubsDetailsEntityNormalized | null | undefined,
  { clubId?: number; accountId?: number }
>({
  key: 'CustomerApplication/clubsDetailsQuery',
  get: ({ clubId, accountId }) => {
    return async () => {
      if (!clubId || !accountId) {
        return;
      }

      const obterClubeData = await Service.External.Extranet.Fundos.V1.fundosObterClube({
        idConta: accountId,
        idProdutoClube: clubId,
        screenName: screenNameCustomerApplication,
      });

      const datasClubeData = await Service.External.Extranet.Fundos.V1.fundosObterDatasClubes({
        idClube: clubId,
        dataOperacao: Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy HH:mm'),
        tipoOperacao: 0,
        screenName: screenNameCustomerApplication,
      });

      const normalizedData = normalizeClubsDetails(
        obterClubeData as ReturnTypeOf_ObterClube,
        datasClubeData as ReturnTypeOf_ObterDatasClubes
      );

      return normalizedData;
    };
  },
});

export const clubsValidateDateQuery = selectorFamily<
  ClubsDateEntityNormalized | null | undefined,
  { clubId: number; accountId: number; date: Date }
>({
  key: 'CustomerApplication/clubsValidateDateQuery',
  get: ({ clubId, accountId, date }) => {
    return async () => {
      if (!clubId || !accountId) {
        return;
      }

      const apiData = await Service.External.Extranet.Fundos.V1.fundosObterDatasClubes({
        idClube: clubId,
        dataOperacao: Utils.Mask.DateFormat(date, 'dd/MM/yyyy HH:mm'),
        tipoOperacao: 0,
        screenName: screenNameCustomerApplication,
      });

      return normalizeClubsDate(apiData as ReturnTypeOf_ObterDatasClubes);
    };
  },
});
